import { Link } from '@tanstack/react-router';
import { Plus, Shapes } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState, FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState } from '@nucleus-commerce/admin-kit/layout';
import { Can } from '@nucleus-commerce/admin-kit/permissions';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Card, CardContent } from '@nucleus-commerce/ui/components/card';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { ProductTypeCreate, ProductTypeEdit } from '../components/product-type-editor';
import { useProductTypes } from '../hooks/use-product-types';

/** Body of the Product types tab; the catalog setup layout supplies the page header. */
function ProductTypes() {
  const { t } = useTranslation('productTypes');
  const query = useProductTypes();
  const rows = query.data;

  // The tour points at the toolbar button; the empty state's copy of it carries no anchor.
  const newButton = (tour?: string) => (
    <Can permission="attributes.write">
      <Button asChild>
        <Link to="/org/catalog-setup/product-types/new" data-tour={tour}>
          <Plus aria-hidden />
          {t('new')}
        </Link>
      </Button>
    </Can>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <p className="text-muted-foreground max-w-prose text-sm">{t('description')}</p>
        {newButton('dictionary.product-types.new-button')}
      </div>
      {query.error ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : !rows ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={Shapes}
          title={t('empty.title')}
          description={t('empty.description')}
          action={newButton()}
          tour="dictionary.product-types.empty"
          tourLink
        />
      ) : (
        <Card className="py-0" data-tour="dictionary.product-types.table">
          <CardContent className="px-0">
            <Table aria-label={t('title')}>
              <TableHeader>
                <TableRow>
                  <TableHead className="pl-6">{t('columns.productType')}</TableHead>
                  <TableHead className="text-right">{t('columns.attributes')}</TableHead>
                  <TableHead className="text-right">{t('columns.required')}</TableHead>
                  <TableHead className="pr-6 text-right">{t('columns.customizations')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((row, index) => (
                  <TableRow key={row.id} aria-label={row.name}>
                    <TableCell className="pl-6">
                      <div className="flex min-w-0 flex-col gap-0.5">
                        <Link
                          to="/org/catalog-setup/product-types/$productTypeId"
                          params={{ productTypeId: row.id }}
                          className="font-medium hover:underline"
                          // The tour's "edit or remove" step points at the first type's link.
                          data-tour={
                            index === 0 ? 'dictionary.product-types.row-actions' : undefined
                          }
                        >
                          {row.name}
                        </Link>
                        <code className="text-muted-foreground text-xs">{row.slug}</code>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.attributes.length}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {row.attributes.filter((a) => a.isRequired).length}
                    </TableCell>
                    <TableCell className="pr-6 text-right text-sm tabular-nums">
                      {row.defaultCustomizationIds.length}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

/**
 * The feature door exports only this page, so the tab's list, new and detail routes all render it
 * and pick the view; `edit` takes the id from the route's params.
 */
export type ProductTypesPageProps =
  { view?: 'list' } | { view: 'new' } | { view: 'edit'; productTypeId: string };

export function ProductTypesPage(props: ProductTypesPageProps) {
  return (
    <FeatureErrorBoundary name="dictionary.productTypes">
      {props.view === 'new' ? (
        <ProductTypeCreate />
      ) : props.view === 'edit' ? (
        <ProductTypeEdit productTypeId={props.productTypeId} />
      ) : (
        <ProductTypes />
      )}
    </FeatureErrorBoundary>
  );
}
